import { useState } from "react";
import { Link } from "react-router-dom";
import { fetchGuestRecommendations } from "../api/client";
import PitchCard from "../components/PitchCard";
import RecoveryPanel from "../components/RecoveryPanel";
import Badge from "../components/ui/Badge";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import EmptyState from "../components/ui/EmptyState";
import LoadingState from "../components/ui/LoadingState";
import { isValidProfile, loadProfile } from "../utils/storage";

export default function GuestMode() {
  const [shows, setShows] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const getVibes = async () => {
    const profile = loadProfile();

    if (!isValidProfile(profile)) {
      setShows([]);
      setActiveProfile(null);
      setStatus({
        title: "Quiz answers needed",
        message: "Take the quiz first so recommendations use your real picks.",
        variant: "warning",
      });
      return;
    }

    try {
      setIsLoading(true);
      setStatus(null);
      const res = await fetchGuestRecommendations(profile);
      const results = res?.data?.results;

      if (!Array.isArray(results)) {
        throw new Error("malformed_recommendation_response");
      }

      setShows(results);
      setActiveProfile(profile);
      setStatus(
        results.length === 0
          ? {
              title: "No matches came back",
              message: "Your saved quiz loaded correctly, but this run did not return matches.",
              variant: "warning",
            }
          : null
      );
    } catch (err) {
      console.error("❌ GuestMode error:", err.response?.data || err);
      setShows([]);
      setActiveProfile(profile);
      setStatus({
        title:
          err.message === "malformed_recommendation_response"
            ? "Recommendations could not be read"
            : "Recommendations did not reload",
        message:
          err.message === "malformed_recommendation_response"
            ? "The server responded, but the results were not in the expected shape."
            : "Could not reload recommendations from your saved quiz profile.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <div>
          <Badge variant="gold" className="mb-5">Guest Mode</Badge>
          <h1 className="font-display text-5xl font-bold leading-tight text-hookd-cream sm:text-6xl">
            Your vibe. Your next obsession.
          </h1>
          <div className="hookd-divider my-6 max-w-md">♕</div>
          <p className="max-w-xl text-lg leading-8 text-hookd-muted">
            Reload your saved quiz profile and get fresh picks without signing in.
          </p>

          <div className="mt-8 grid gap-3 sm:flex">
            <Button
              type="button"
              onClick={getVibes}
              loading={isLoading}
              className="w-full sm:w-auto"
              size="lg"
            >
              Get Vibes
            </Button>

            <Button as={Link} to="/quiz" variant="outline" className="w-full sm:w-auto" size="lg">
              Take the Quiz
            </Button>
          </div>
        </div>

        <Card className="p-6 sm:p-8" glow="gold">
          <div className="grid gap-5 sm:grid-cols-2">
            {[
              ["Personalized Picks", "Shows and movies that match your existing quiz energy."],
              ["Fast Shortcut", "No extra setup when your saved answers are ready."],
              ["Fresh Runs", "Retry the same profile when you want a new batch."],
              ["Save Later", "Create an account after the recs land."],
            ].map(([title, copy]) => (
              <div key={title} className="rounded-hookd border border-hookd-line bg-white/5 p-4">
                <p className="mb-2 text-hookd-gold">✧</p>
                <h2 className="font-bold text-hookd-cream">{title}</h2>
                <p className="mt-2 text-sm leading-6 text-hookd-muted">{copy}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {status && (
        <div className="mt-8">
          <RecoveryPanel
            title={status.title}
            message={status.message}
            variant={status.variant}
            actions={[
              isValidProfile(activeProfile)
                ? {
                    label: "Retry",
                    onClick: getVibes,
                    disabled: isLoading,
                  }
                : {
                    label: "Take the Quiz",
                    to: "/quiz",
                  },
            ]}
          />
        </div>
      )}

      {isLoading && (
        <div className="mt-8">
          <LoadingState title="Finding Your Guest Picks" message="Reloading your saved vibe profile." />
        </div>
      )}

      <div className="mt-8 grid gap-5">
        {shows.length > 0 ? (
          shows.map((show) => (
            <PitchCard
              key={show.title}
              show={show}
              guestId={activeProfile?.guest_id || null}
            />
          ))
        ) : !isLoading && !status ? (
          <EmptyState
            title="Ready when your quiz is"
            message="Guest Mode needs saved quiz answers first. Take the quiz once, then this shortcut can reload your vibe."
            primaryAction={{ label: "Take the Quiz", to: "/quiz" }}
          />
        ) : null}
      </div>
    </div>
  );
}
